import {
  formatearExcel,
  parsearNumeroExcel,
} from '@/shared/formula/excelFormat';
import { migrateGlobalFormatKey } from '@/shared/formula/globalFormats';
import type { GridFormats } from './formats';
import { DEFAULT_FORMATS } from './formats';

export type SummaryCardCalculationType =
  | 'SUM'
  | 'AVERAGE'
  | 'COUNT'
  | 'COUNTA'
  | 'MIN'
  | 'MAX'
  | 'CUSTOM';

export interface SummaryCardConfig {
  readonly id: string;
  readonly label: string;
  readonly calculationType: SummaryCardCalculationType;
  /** Column field/id used for the aggregation (when calculationType !== 'CUSTOM'). */
  readonly columnId?: string | undefined;
  /** Custom static value or formula description when calculationType === 'CUSTOM'. */
  readonly customValue?: string | undefined;
  /** Number format key or custom pattern, e.g. 'currency', 'integer', 'percent'. */
  readonly format?: string | undefined;
  /** Optional prefix text, e.g. '$', '€', etc. */
  readonly prefix?: string | undefined;
  /** Optional suffix text, e.g. ' USD', ' items', '%', etc. */
  readonly suffix?: string | undefined;
  /** Number of decimal places (optional override). */
  readonly decimals?: number | undefined;
}

/** Per grid key (e.g. `sheet:${tabId}` or `investments`) -> array of configured summary cards */
export type GridSummaryCardsConfig = Record<string, readonly SummaryCardConfig[]>;

export function normalizeSummaryCardConfig(raw: unknown): SummaryCardConfig | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const item = raw as Record<string, unknown>;
  const id = typeof item.id === 'string' && item.id.trim() !== '' ? item.id.trim() : `card_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  const label = typeof item.label === 'string' ? item.label : '';
  const validTypes: Set<SummaryCardCalculationType> = new Set([
    'SUM',
    'AVERAGE',
    'COUNT',
    'COUNTA',
    'MIN',
    'MAX',
    'CUSTOM',
  ]);
  const rawType = typeof item.calculationType === 'string' ? (item.calculationType.toUpperCase() as SummaryCardCalculationType) : 'SUM';
  const calculationType: SummaryCardCalculationType = validTypes.has(rawType) ? rawType : 'SUM';
  const columnId = typeof item.columnId === 'string' && item.columnId !== '' ? item.columnId : undefined;
  const customValue = typeof item.customValue === 'string' ? item.customValue : undefined;
  const format = typeof item.format === 'string' && item.format !== '' ? item.format : undefined;
  const prefix = typeof item.prefix === 'string' ? item.prefix : undefined;
  const suffix = typeof item.suffix === 'string' ? item.suffix : undefined;
  const decimals = typeof item.decimals === 'number' && Number.isFinite(item.decimals) && item.decimals >= 0 ? Math.min(10, Math.floor(item.decimals)) : undefined;

  return {
    id,
    label,
    calculationType,
    ...(columnId !== undefined ? { columnId } : {}),
    ...(customValue !== undefined ? { customValue } : {}),
    ...(format !== undefined ? { format } : {}),
    ...(prefix !== undefined ? { prefix } : {}),
    ...(suffix !== undefined ? { suffix } : {}),
    ...(decimals !== undefined ? { decimals } : {}),
  };
}

export function normalizeGridSummaryCards(raw: unknown): GridSummaryCardsConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const result: Record<string, SummaryCardConfig[]> = {};

  for (const [gridKey, rawList] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(rawList)) continue;
    const cards: SummaryCardConfig[] = [];
    for (const item of rawList) {
      const normalized = normalizeSummaryCardConfig(item);
      if (normalized) cards.push(normalized);
    }
    if (cards.length > 0) {
      result[gridKey] = cards;
    }
  }
  return result;
}

/**
 * Calculates the dynamic display value for a summary card given sheet rows and column configurations.
 */
export function calculateSummaryCardValue(
  card: SummaryCardConfig,
  rows: readonly Record<string, unknown>[],
  formats: GridFormats = DEFAULT_FORMATS,
): string {
  const { calculationType, columnId, customValue, format, prefix = '', suffix = '', decimals } = card;

  if (calculationType === 'CUSTOM') {
    const base = customValue ?? '';
    return `${prefix}${base}${suffix}`.trim();
  }

  if (!columnId) {
    // If no column is selected, but calculation is COUNT, count rows
    if (calculationType === 'COUNT' || calculationType === 'COUNTA') {
      const count = rows.length;
      return `${prefix}${formatNumberValue(count, format, decimals, formats)}${suffix}`.trim();
    }
    return '—';
  }

  // Extract cell values for the column
  const rawValues = rows.map((r) => r[columnId]);

  if (calculationType === 'COUNTA') {
    const nonBlankCount = rawValues.filter((v) => v !== null && v !== undefined && v !== '').length;
    return `${prefix}${formatNumberValue(nonBlankCount, format, decimals, formats)}${suffix}`.trim();
  }

  if (calculationType === 'COUNT') {
    // COUNT counts numeric values
    let numericCount = 0;
    for (const v of rawValues) {
      const num = toNumeric(v, formats);
      if (num !== null) numericCount++;
    }
    return `${prefix}${formatNumberValue(numericCount, format, decimals, formats)}${suffix}`.trim();
  }

  const computed = aggregateSummaryNumber(card, rows, formats);
  if (computed === null) {
    return `${prefix}0${suffix}`.trim();
  }

  const formattedValue = formatNumberValue(computed, format, decimals, formats);
  return `${prefix}${formattedValue}${suffix}`.trim();
}

/**
 * The raw result of a SUM, AVERAGE, MIN or MAX card, before any formatting; `null`
 * for the other calculations or when the column holds no numbers.
 */
export function aggregateSummaryNumber(
  card: SummaryCardConfig,
  rows: readonly Record<string, unknown>[],
  formats: GridFormats = DEFAULT_FORMATS,
): number | null {
  const { calculationType, columnId } = card;
  if (!columnId) return null;
  if (!['SUM', 'AVERAGE', 'MIN', 'MAX'].includes(calculationType)) return null;
  const numbers: number[] = [];
  for (const row of rows) {
    const num = toNumeric(row[columnId], formats);
    if (num !== null) numbers.push(num);
  }
  if (numbers.length === 0) return null;
  if (calculationType === 'SUM') return numbers.reduce((a, b) => a + b, 0);
  if (calculationType === 'AVERAGE') return numbers.reduce((a, b) => a + b, 0) / numbers.length;
  if (calculationType === 'MIN') return Math.min(...numbers);
  return Math.max(...numbers);
}

function toNumeric(value: unknown, formats: GridFormats): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') {
    const parsed = parsearNumeroExcel(value, formats.integer);
    if (parsed !== null && Number.isFinite(parsed)) return parsed;
    const direct = Number(value.replace(/\s+/g, '').replace(/,/g, '.'));
    if (Number.isFinite(direct)) return direct;
  }
  return null;
}

function formatNumberValue(
  val: number,
  formatKey?: string,
  decimals?: number,
  formats: GridFormats = DEFAULT_FORMATS,
): string {
  if (decimals !== undefined) {
    // Custom decimal places pattern
    const decPattern = decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0';
    const pattern = `#,##${decPattern}`;
    return formatearExcel(val, pattern);
  }

  // A card saved before `quantity` split into `integer` and `decimal` still says so.
  const key = formatKey === undefined ? undefined : migrateGlobalFormatKey(formatKey);
  if (key && key in formats) {
    const pattern = formats[key as keyof GridFormats];
    return formatearExcel(val, pattern);
  }

  return formatearExcel(val, formats.integer || '#,##0.##');
}
